import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { CheckCircle2, ChevronDown, ChevronUp, Loader2, Tag, Truck, X } from "lucide-react";
import ScheduledItemsSection from "./ScheduledItemsSection";
import InstallmentPlanSection from "./InstallmentPlanSection";
import { getDeliveryTypeTotalUtil } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { cn, couponBucketForItem, isProgrammedOrderItem, isDeferredProgrammedOrderItem, mapEtapaToGradeName, showsProgrammedListValue } from "@/lib/utils";
import { isNewScheduledOrderCycleEnabledForCycle } from "@/features/newScheduledOrderCycleFeature";
import { couponApi, type CouponValidation } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { isCouponEnabled } from "@/features/couponFeature";
import { buildCouponBonifiedLines, hasCouponUnits } from "@/lib/couponItems";
import { useUser } from "@/contexts/UserContext";

interface OrderItem {
  id: number;
  product_id?: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  quantidade_contratual: number;
  desconto: number;
  quantidade_pedido: number;
  valor_liquido: number;
  valor_total: number;
  delivery_type_id: number;
  delivery_type_number: number;
  brand_id: number;
  product_type: number;
  bonified_quantity?: number;
  scheduling_category?: string;
}

interface OrderSummaryProps {
  cycleYear?: number;
  grossSubtotal: number;
  netSubtotal: number;
  total: number;
  freteAnual: number; // Added
  freteSemestral: number; // Added
  orderItems: OrderItem[];
  formatCurrency: (value: number) => string;
  finalizarPedido?: (reason?: string, coupon?: CouponValidation | null) => void;
  isScheduled: boolean;
  isBackofficeEdit?: boolean;
  isEditMode?: boolean;
  onAnnualInstallmentsChange?: (value: number) => void;
  onSemestralInstallmentsChange?: (value: number) => void;
  onAnnualConditionChange?: (value: string | null) => void;
  onSemestralConditionChange?: (value: string | null) => void;
  hideFinalizeButton?: boolean;
  hideInstallmentPlan?: boolean;
  /** Cupom por itens: desconto por produto (id do produto → valor), para os totais por segmento. Vazio sem cupom. */
  onCouponItemDiscountsChange?: (discountByProduct: Record<number, number>) => void;
  /** Escolha do endereço de entrega (customerGroupAddressFeature), exibida acima do cupom. */
  shippingAddressSlot?: React.ReactNode;
}

const OrderSummary = ({
  cycleYear,
  total,
  freteAnual, // Destructure freteAnual
  freteSemestral, // Destructure freteSemestral
  orderItems,
  formatCurrency,
  finalizarPedido,
  isScheduled,
  isBackofficeEdit = false,
  isEditMode = false,
  onAnnualInstallmentsChange,
  onSemestralInstallmentsChange,
  onAnnualConditionChange,
  onSemestralConditionChange,
  hideFinalizeButton = false,
  hideInstallmentPlan = false,
  onCouponItemDiscountsChange,
  shippingAddressSlot,
}: OrderSummaryProps) => {
  const usesDeferredSecondSemesterCharge = !isScheduled
    && isNewScheduledOrderCycleEnabledForCycle(cycleYear);
  const chargeableOrderItems = usesDeferredSecondSemesterCharge
    ? orderItems.filter(item => !isDeferredProgrammedOrderItem(item, cycleYear))
    : orderItems;
  const { selectedClient } = useUser();
  const couponClientCode = selectedClient?.codigo_cnx ?? null;
  // Itens que entram no cálculo do cupom: os faturados agora (anual e semestral) e os de 2º semestre programado (cupom por
  // item: o desconto fica reservado e é abatido na cobrança do programado). Avaliações programadas não entram.
  const couponScheduledOrderItems = usesDeferredSecondSemesterCharge
    ? orderItems.filter(item => isDeferredProgrammedOrderItem(item, cycleYear) && item.scheduling_category !== "ExamScheduled")
    : [];
  const couponEligibleItems = [...chargeableOrderItems, ...couponScheduledOrderItems]
    .filter(item => item.quantidade_pedido > 0 && item.valor_total > 0);
  const couponBucketOf = (item: OrderItem) =>
    couponBucketForItem(item, cycleYear, usesDeferredSecondSemesterCharge);
  const couponItemsSignature = `${couponClientCode ?? ""}#` + couponEligibleItems
    .map(item => `${couponBucketOf(item)}:${item.product_id ?? item.id}:${item.codigo_produto}:${item.quantidade_pedido}:${item.bonified_quantity ?? 0}:${item.valor_total}`)
    .join("|");
  const [isAnualExpanded, setIsAnualExpanded] = useState(false);
  const [isSemestralExpanded, setIsSemestralExpanded] = useState(false);
  const [isProgramadosExpanded, setIsProgramadosExpanded] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [editReason, setEditReason] = useState("");
  const [reasonError, setReasonError] = useState(false);
  const [isInstallmentsLoading, setIsInstallmentsLoading] = useState(!isEditMode);
  const [hasInstallmentData, setHasInstallmentData] = useState(isEditMode);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<CouponValidation | null>(null);
  // Bucket da alocação; se a API não devolver (versão antiga), usa o do próprio item pelo itemKey.
  const allocationBucketOf = (allocation: { itemKey: string; bucket?: string }) => {
    if (allocation.bucket) return allocation.bucket;
    const item = couponEligibleItems.find((candidate, i) => `${candidate.id}-${i}` === allocation.itemKey);
    return item ? couponBucketOf(item) : "Annual";
  };
  // Desconto imediato por tipo de pedido: o cupom é abatido antes do cálculo das parcelas.
  const couponDiscountByBucket = (bucket: "Annual" | "Semestral") =>
    appliedCoupon
      ? Math.round(appliedCoupon.allocations
        .filter(allocation => allocationBucketOf(allocation) === bucket)
        .reduce((sum, allocation) => sum + allocation.discountAmount, 0) * 100) / 100
      : 0;
  // Desconto abatido agora; sem a quebra do backend, vale o total (comportamento anterior).
  const appliedCouponNow = appliedCoupon
    ? (appliedCoupon.immediateDiscountAmount ?? appliedCoupon.discountAmount)
    : 0;
  // Cupom por itens: unidades bonificadas pelo cupom, listadas no resumo (descrição do item do pedido).
  const isImmediateAllocation = (allocation: { itemKey: string; bucket?: string }) =>
    ["Annual", "Semestral"].includes(allocationBucketOf(allocation));
  const describeCouponAllocation = (allocation: { itemKey: string }) =>
    couponEligibleItems.find((candidate, i) => `${candidate.id}-${i}` === allocation.itemKey)?.descricao_produto;
  const couponAllocationDetails = (allocation: { itemKey: string }) => {
    const item = couponEligibleItems.find((candidate, i) => `${candidate.id}-${i}` === allocation.itemKey);
    // A série vem como código da etapa ("1"); o nome ("Grupo 1", "1º ano"...) é o mesmo exibido nos cards de itens.
    return item ? { segment: item.segmento, series: item.serie ? mapEtapaToGradeName(item.serie) : null } : undefined;
  };
  const couponBonifiedLines = buildCouponBonifiedLines(
    hasCouponUnits(appliedCoupon) ? appliedCoupon?.allocations.filter(isImmediateAllocation) : undefined,
    describeCouponAllocation,
    couponAllocationDetails,
  );
  // Itens de 2º semestre cobertos pelo cupom: não entram na conta do pedido agora (o desconto é abatido na cobrança
  // dos programados), mas são listados à parte para conferir que o cupom está sendo usado neles.
  const couponScheduledLines = buildCouponBonifiedLines(
    hasCouponUnits(appliedCoupon)
      ? appliedCoupon?.allocations.filter(allocation => allocationBucketOf(allocation) === "SecondSemester")
      : undefined,
    describeCouponAllocation,
    couponAllocationDetails,
  );
  // Itens de 2º semestre cobertos pelo cupom: mostrados dentro do bloco do pedido programado.
  const couponScheduledTable = couponScheduledLines.length > 0 ? (
        <div className="mt-1 rounded-md border border-emerald-200 bg-white/70 p-3">
          <p className="mb-1 text-xs font-bold uppercase tracking-wide text-emerald-800">Itens de 2º semestre cobertos pelo cupom</p>
          <table className="w-full table-fixed text-left text-sm" aria-label="Itens de 2º semestre cobertos pelo cupom">
            <colgroup>
              <col className="w-14" />
              <col />
              <col className="w-[28%]" />
              <col className="w-[18%]" />
            </colgroup>
            <thead>
              <tr className="border-b border-emerald-200 text-xs uppercase tracking-wide text-emerald-800">
                <th scope="col" className="w-14 pb-1.5 pr-3 font-semibold">Qtd.</th>
                <th scope="col" className="pb-1.5 pr-3 font-semibold">Produto</th>
                <th scope="col" className="pb-1.5 pr-3 font-semibold">Segmento</th>
                <th scope="col" className="pb-1.5 font-semibold">Série</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-emerald-100">
              {couponScheduledLines.map(line => (
                <tr key={line.itemKey} className="align-top">
                  <td className="py-2 pr-3">
                    <span className="rounded-md bg-emerald-600 px-2 py-0.5 text-xs font-bold text-white">{line.quantity}</span>
                  </td>
                  <td className="py-2 pr-3 font-medium">{line.description}</td>
                  <td className="py-2 pr-3">{line.segment || "—"}</td>
                  <td className="py-2">{line.series || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
  ) : null;
  // Desconto do cupom por itens agrupado por produto, para o resumo por segmento abater o que o cupom cobre.
  const couponDiscountByProduct: Record<number, number> = {};
  if (appliedCoupon && hasCouponUnits(appliedCoupon)) {
    appliedCoupon.allocations.filter(isImmediateAllocation).forEach(allocation => {
      const item = couponEligibleItems.find((candidate, i) => `${candidate.id}-${i}` === allocation.itemKey);
      const productId = item ? (item.product_id ?? item.id) : undefined;
      if (productId != null && allocation.discountAmount > 0) {
        couponDiscountByProduct[productId] = Math.round(((couponDiscountByProduct[productId] ?? 0) + allocation.discountAmount) * 100) / 100;
      }
    });
  }
  const couponDiscountByProductKey = JSON.stringify(couponDiscountByProduct);
  useEffect(() => {
    onCouponItemDiscountsChange?.(JSON.parse(couponDiscountByProductKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponDiscountByProductKey]);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [validatedOrderTotal, setValidatedOrderTotal] = useState<number | null>(null);
  const [validatedItemsSignature, setValidatedItemsSignature] = useState<string | null>(null);

  const handleInstallmentLoadingStateChange = (loading: boolean, hasData: boolean) => {
    setIsInstallmentsLoading(loading);
    setHasInstallmentData(hasData);
  };

  useEffect(() => {
    if (appliedCoupon && (validatedOrderTotal !== total || validatedItemsSignature !== couponItemsSignature)) {
      setAppliedCoupon(null);
    }
  }, [total, couponItemsSignature, appliedCoupon, validatedOrderTotal, validatedItemsSignature]);

  // Mensagem do backend para cupom de valor fixo maior que o pedido: exibida abaixo do cupom, não como alerta.
  const COUPON_ABOVE_ORDER_MESSAGE = "O cupom não pode ser utilizado nesse pedido. Verifique as informações do seu pedido";
  // Trava de faturamento (90% do previsto): mesma caixa de erro do cupom (texto igual ao de CouponMessages.RevenueBelowMinimum na API).
  const COUPON_REVENUE_MESSAGE = "O cupom não pode ser utilizado nesse pedido. Verifique as informações do seu pedido.";
  const [couponError, setCouponError] = useState<string | null>(null);

  const handleApplyCoupon = async () => {
    setCouponError(null);
    const normalizedCode = couponCode.trim().toUpperCase();
    if (!normalizedCode) {
      toast({ title: "Informe um cupom", description: "Digite o código recebido para continuar.", variant: "destructive" });
      return;
    }

    setIsApplyingCoupon(true);
    try {
      const coupon = await couponApi.validate(
        normalizedCode,
        couponEligibleItems
          .map((item, index) => ({
            itemKey: `${item.id}-${index}`,
            bucket: couponBucketOf(item),
            productId: item.product_id ?? item.id,
            productCode: item.codigo_produto,
            productsSubtotal: item.valor_total,
            quantity: item.quantidade_pedido,
            bonifiedQuantity: item.bonified_quantity ?? 0,
          })),
        couponClientCode,
        cycleYear ?? null,
      );
      setCouponCode(coupon.code);
      setAppliedCoupon(coupon);
      setValidatedOrderTotal(total);
      setValidatedItemsSignature(couponItemsSignature);
      toast({ title: "Cupom aplicado", description: `${coupon.name} foi aplicado aos produtos do pedido.` });
    } catch (error) {
      setAppliedCoupon(null);
      setValidatedOrderTotal(null);
      setValidatedItemsSignature(null);
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? "Não foi possível validar este cupom.";
      if (message === COUPON_ABOVE_ORDER_MESSAGE || message === COUPON_REVENUE_MESSAGE) {
        setCouponError(message);
      } else {
        toast({ title: "Cupom inválido", description: message, variant: "destructive" });
      }
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponError(null);
    setAppliedCoupon(null);
    setCouponCode("");
    setValidatedOrderTotal(null);
    setValidatedItemsSignature(null);
  };

  const handleFinalizarClick = () => {
    if (isScheduled) {
      setIsConfirmDialogOpen(true);
    } else {
      finalizarPedido?.(undefined, appliedCoupon);
    }
  };

  const handleConfirmFinalize = () => {
    if (isBackofficeEdit && !editReason.trim()) {
      setReasonError(true);
      toast({
        title: "Campo Obrigatório",
        description: "O motivo da edição é obrigatório para usuários do Backoffice.",
        variant: "destructive",
      });
      return;
    }

    setIsConfirmDialogOpen(false);
    finalizarPedido?.(editReason, appliedCoupon);
  };

  // Keep student and teacher materials separate, even with identical descriptions.
  const groupProductsByDescription = (items: OrderItem[]) => {
    const grouped = items.reduce((acc, item) => {
      // Create a composite key: description + serie to group by both
      const key = JSON.stringify([item.marca, item.descricao_produto, item.sku ?? "", item.serie || "sem-serie", item.utilizacao]);
      if (!acc[key]) {
        acc[key] = {
          ...item,
          quantidade_pedido: 0,
          valor_total: 0,
          bonified_quantity: 0,
        };
      }
      acc[key].quantidade_pedido += item.quantidade_pedido;
      acc[key].valor_total += item.valor_total;
      acc[key].bonified_quantity =
        (acc[key].bonified_quantity ?? 0) + (item.bonified_quantity ?? 0);
      return acc;
    }, {} as Record<string, OrderItem>);

    // Recalculate valor_liquido after grouping
    return Object.values(grouped).map((item) => ({
      ...item,
      valor_liquido:
        item.quantidade_pedido > 0
          ? item.valor_total / item.quantidade_pedido
          : 0,
    }));
  };

  // Map marca (which contains ID as string) to brand name
  const getBrandName = (marca: string): string => {
    const brandMap: Record<string, string> = {
      "1": "AZ",
      "2": "High Five",
      "3": "My Life",
      "4": "LEX",
      "5": "AI4SCHOOL",
    };
    return brandMap[marca] || marca || "Sem Marca";
  };

  // Group products by brand
  const groupProductsByBrand = (items: OrderItem[]) => {
    // First aggregate products with identical descriptions
    const aggregatedProducts: Record<string, OrderItem> = {};

    items.forEach((item) => {
      const key = JSON.stringify([
        item.marca,
        item.descricao_produto,
        item.sku ?? "",
        item.serie,
        item.utilizacao,
      ]);

      if (aggregatedProducts[key]) {
        // Aggregate quantities and totals
        aggregatedProducts[key].quantidade_pedido += item.quantidade_pedido;
        aggregatedProducts[key].valor_total += item.valor_total;
        aggregatedProducts[key].quantidade_contratual +=
          item.quantidade_contratual;
        aggregatedProducts[key].bonified_quantity =
          (aggregatedProducts[key].bonified_quantity ?? 0) + (item.bonified_quantity ?? 0);
      } else {
        // Create new entry
        aggregatedProducts[key] = { ...item };
      }
    });

    const aggregatedItems = Object.values(aggregatedProducts);

    // Group by brand using the marca field directly
    const groupedByBrand = aggregatedItems.reduce((acc, item) => {
      const brandName = getBrandName(item.marca);
      const brandKey = brandName;

      if (!acc[brandKey]) {
        acc[brandKey] = {
          brandId: item.brand_id,
          brandName: brandName,
          products: [],
        };
      }

      acc[brandKey].products.push(item);

      return acc;
    }, {} as Record<string, { brandId: number; brandName: string; products: OrderItem[] }>);

    // Sort by brand_id and create the final structure
    return Object.values(groupedByBrand)
      .sort((a, b) => a.brandId - b.brandId)
      .map(({ brandName, products }) => {
        // Sort products: students first, then teachers, then alphabetically
        const sortedProducts = products.sort((a, b) => {
          // First sort by utilizacao (alunos first, then professores)
          if (a.utilizacao !== b.utilizacao) {
            const aIsTeacher = a.utilizacao === "Professor" ? 1 : 0;
            const bIsTeacher = b.utilizacao === "Professor" ? 1 : 0;
            return aIsTeacher - bIsTeacher;
          }
          // Then sort by description (alphabetical)
          const descA = String(a.descricao_produto || "");
          const descB = String(b.descricao_produto || "");
          return descA.localeCompare(descB);
        });

        const subtotal = products.reduce(
          (sum, item) => sum + item.valor_total,
          0
        );

        return {
          brandName,
          products: sortedProducts,
          subtotal,
        };
      });
  };

  // Get products by delivery type with proper sorting and grouping
  const getProductsByDeliveryType = (deliveryTypeNumber: number) => {
    const filtered = orderItems.filter((item) => {
      // Remover itens sem quantidades
      if (!item.quantidade_pedido || item.quantidade_pedido <= 0) return false;

      // Filter by delivery type
      const isMatch = (item.delivery_type_number === deliveryTypeNumber) || (item.delivery_type_id === deliveryTypeNumber);
      if (!isMatch) return false;

      if (usesDeferredSecondSemesterCharge && isDeferredProgrammedOrderItem(item, cycleYear)) {
        return false;
      }

      // Filter by delivery type only (keep items even with quantidade_pedido = 0)
      if (item.delivery_type_number !== deliveryTypeNumber) {
        return false;
      }

      // Exclude "Plataforma AZ - 2º Semestre" from semestral products
      // if (
      //   deliveryTypeNumber === 2 &&
      //   item.descricao_produto.includes( "Plataforma AZ" ) &&
      //   item.descricao_produto.includes( "2º Semestre" )
      // ) {
      //   return false;
      // }

      // const isSecondSemester = item.descricao_produto.includes( "2º Semestre" );
      // if ( !isScheduled && isSecondSemester ) return false;
      return true;
    });

    // Group by description first
    const grouped = groupProductsByDescription(filtered);

    // Then apply the grade/series grouping which includes all items
    return groupProductsByBrand(grouped);
  };

  // Calculate totals by delivery type
  const getDeliveryTypeTotal = (deliveryTypeNumber: number) => {
    return getDeliveryTypeTotalUtil(chargeableOrderItems, deliveryTypeNumber, isScheduled);
  };

  // Get scheduled order items with brand grouping
  const getScheduledItems = () => {
    // Lista informativa do "Pedido programado": quantidade de todos os itens e valor só dos itens de
    // 2º semestre (avaliações/simulados ficam sem valor). Esses valores não entram no total do pedido
    // atual (ver chargeableOrderItems). Só as cópias de apresentação são alteradas; o preço vigente
    // permanece no item original para ser persistido no pedido programado.
    const scheduledItems = orderItems
      .filter(
        (item) =>
          isProgrammedOrderItem(item, cycleYear) &&
          item.quantidade_pedido > 0
      )
      .map((item) => showsProgrammedListValue(item)
        ? item
        : { ...item, preco: 0, valor_liquido: 0, valor_total: 0 });

    let studentTotal = 0;
    let teacherTotal = 0;
    let studentQuantity = 0;
    let teacherQuantity = 0;
    let totalValue = 0;
    let totalQuantity = 0;

    scheduledItems.forEach((item) => {
      const itemValue = item.valor_liquido * item.quantidade_pedido;
      totalValue += itemValue;
      totalQuantity += item.quantidade_pedido;

      if (item.utilizacao === "Professor") {
        teacherTotal += itemValue;
        teacherQuantity += item.quantidade_pedido;
      } else {
        studentTotal += itemValue;
        studentQuantity += item.quantidade_pedido;
      }
    });

    // Group scheduled items by description first, then apply brand grouping
    const groupedScheduledItems = groupProductsByDescription(scheduledItems);
    const brandGroupedScheduledItems = groupProductsByBrand(
      groupedScheduledItems
    );

    return {
      hasScheduledItems: scheduledItems.length > 0,
      studentTotal,
      teacherTotal,
      studentQuantity,
      teacherQuantity,
      totalValue,
      totalQuantity,
      brandGroups: brandGroupedScheduledItems,
    };
  };

  const produtosAnuais = getProductsByDeliveryType(1);
  const produtosSemestrais = getProductsByDeliveryType(2);
  const totalProdutosAnuais = getDeliveryTypeTotal(1);
  const totalProdutosSemestrais = getDeliveryTypeTotal(2);
  const {
    hasScheduledItems,
    studentTotal,
    teacherTotal,
    studentQuantity,
    teacherQuantity,
    totalValue,
    totalQuantity,
    brandGroups,
  } = getScheduledItems();

  return (
    <div className="space-y-6">
      {/* Resumo de Valores */}
      <Card className="shadow-lg bg-primary/5 border-primary/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl text-primary">
            💰 Resumo de Valores
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-6">
            {/* Pedido Anual */}
            {produtosAnuais.length > 0 && (
              <>
                <Collapsible
                  open={isAnualExpanded}
                  onOpenChange={setIsAnualExpanded}
                >
                  <div className="space-y-3">
                    <CollapsibleTrigger asChild>
                      <div className="flex justify-between items-center cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <span className="font-medium">{isScheduled ? "📦 Itens do Pedido" : "📅 Pedido Anual"}</span>
                          <span className="text-xs bg-muted px-2 py-1 rounded-full">
                            {produtosAnuais.length}{" "}
                            {produtosAnuais.length === 1 ? "produto" : "produtos"}
                          </span>
                          {isAnualExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </div>
                        <span className="font-bold text-lg">
                          {formatCurrency(totalProdutosAnuais)}
                        </span>
                      </div>
                    </CollapsibleTrigger>

                    <CollapsibleContent className="space-y-2">
                      {produtosAnuais.length > 0 && (
                        <div className="ml-4 space-y-3">
                          {produtosAnuais.map((brandGroup, brandIndex) => (
                            <div key={`brand-${brandIndex}`} className="space-y-2">
                              <div className="flex justify-between items-center py-2 px-3 bg-muted/30 rounded-lg border-l-4 border-primary/30">
                                <span className="font-medium text-primary text-sm">
                                  📚 {brandGroup.brandName}
                                </span>
                                <span className="font-semibold text-primary text-sm">
                                  {formatCurrency(brandGroup.subtotal)}
                                </span>
                              </div>
                              <div className="ml-4 space-y-1 text-sm text-muted-foreground">
                                {brandGroup.products.map((item) => (
                                  <div
                                    key={item.id}
                                    className="flex justify-between py-1 pr-3 border-b border-border/30 last:border-0"
                                  >
                                    <span className="truncate flex-1">
                                      {item.descricao_produto}
                                      <Badge variant="outline" className="ml-2 text-xs">
                                        {item.utilizacao === "Professor" ? "Professor" : item.utilizacao || "Aluno"}
                                      </Badge>
                                    </span>
                                    <span className="ml-2 whitespace-nowrap font-medium">
                                      {formatCurrency(item.valor_total)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CollapsibleContent>
                  </div>
                </Collapsible>
                <Separator />
              </>
            )}

            {/* Pedido Semestre */}
            {produtosSemestrais.length > 0 && (
              <>
                <Collapsible
                  open={isSemestralExpanded}
                  onOpenChange={setIsSemestralExpanded}
                >
                  <div className="space-y-3">
                    <CollapsibleTrigger asChild>
                      <div className="flex justify-between items-center cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <span className="font-medium">{isScheduled ? "📦 Itens do Pedido" : "📆 Pedido Semestral"}</span>
                          <span className="text-xs bg-muted px-2 py-1 rounded-full">
                            {produtosSemestrais.length}{" "}
                            {produtosSemestrais.length === 1
                              ? "produto"
                              : "produtos"}
                          </span>
                          {isSemestralExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </div>
                        <span className="font-bold text-lg">
                          {formatCurrency(totalProdutosSemestrais)}
                        </span>
                      </div>
                    </CollapsibleTrigger>

                    <CollapsibleContent className="space-y-2">
                      {produtosSemestrais.length > 0 && (
                        <div className="ml-4 space-y-3">
                          {produtosSemestrais.map((brandGroup, brandIndex) => (
                            <div key={`brand-${brandIndex}`} className="space-y-2">
                              <div className="flex justify-between items-center py-2 px-3 bg-muted/30 rounded-lg border-l-4 border-blue-500/30">
                                <span className="font-medium text-blue-600 text-sm">
                                  📚 {brandGroup.brandName}
                                </span>
                                <span className="font-semibold text-blue-600 text-sm">
                                  {formatCurrency(brandGroup.subtotal)}
                                </span>
                              </div>
                              <div className="ml-4 space-y-1 text-sm text-muted-foreground">
                                {brandGroup.products.map((item) => (
                                  <div
                                    key={item.id}
                                    className="flex justify-between py-1 pr-3 border-b border-border/30 last:border-0"
                                  >
                                    <span className="truncate flex-1">
                                      {item.descricao_produto}
                                      <Badge variant="outline" className="ml-2 text-xs">
                                        {item.utilizacao === "Professor" ? "Professor" : item.utilizacao || "Aluno"}
                                      </Badge>
                                    </span>
                                    <span className="ml-2 whitespace-nowrap font-medium">
                                      {formatCurrency(item.valor_total)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CollapsibleContent>
                  </div>
                </Collapsible>

                <Separator />
              </>
            )}

            {(!!freteSemestral || !!freteAnual) && !isScheduled && (
              <div className="flex justify-between items-center text-md">
                <div className="flex ml-1 items-center gap-2 text-muted-foreground">
                  <Truck className="h-5 w-5" />
                  <span>Valor do Frete</span>
                </div>
                <span className="font-semibold">{formatCurrency((freteAnual + freteSemestral))}</span>
              </div>
            )}
            {/* {freteSemestral > 0 && (
              <div className="flex justify-between items-center text-md">
                <div className="flex ml-1 items-center gap-2 text-muted-foreground">
                  <Truck className="h-5 w-5" />
                  <span>Valor do Frete (Semestral)</span>
                </div>
                <span className="font-semibold">{formatCurrency( freteSemestral )}</span>
              </div>
            )} */}

            {shippingAddressSlot}

            {isCouponEnabled && !isScheduled && !isEditMode && !hideFinalizeButton && (
              <div className={`space-y-3 rounded-lg border p-4 ${couponError ? "border-destructive/30 bg-destructive/5" : "bg-muted/20"}`}>
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold">Inserir Cupom</span>
                </div>
                {appliedCoupon ? (
                  <div className="flex items-start justify-between gap-3 rounded-lg border-2 border-emerald-300 bg-emerald-50 p-4 text-emerald-950">
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-base font-bold">{appliedCoupon.code} · {appliedCoupon.name}</p>
                        {couponBonifiedLines.length > 0 && (
                          <div className="mt-3 rounded-md border border-emerald-200 bg-white/70 p-3">
                            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-emerald-800">Itens cobertos pelo cupom</p>
                            <table className="w-full table-fixed text-left text-sm" aria-label="Itens cobertos pelo cupom">
                              <colgroup>
                                <col className="w-14" />
                                <col />
                                <col className="w-[28%]" />
                                <col className="w-[18%]" />
                              </colgroup>
                              <thead>
                                <tr className="border-b border-emerald-200 text-xs uppercase tracking-wide text-emerald-800">
                                  <th scope="col" className="w-14 pb-1.5 pr-3 font-semibold">Qtd.</th>
                                  <th scope="col" className="pb-1.5 pr-3 font-semibold">Produto</th>
                                  <th scope="col" className="pb-1.5 pr-3 font-semibold">Segmento</th>
                                  <th scope="col" className="pb-1.5 font-semibold">Série</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-emerald-100">
                                {couponBonifiedLines.map(line => (
                                  <tr key={line.itemKey} className="align-top">
                                    <td className="py-2 pr-3">
                                      <span className="rounded-md bg-emerald-600 px-2 py-0.5 text-xs font-bold text-white">{line.quantity}</span>
                                    </td>
                                    <td className="py-2 pr-3 font-medium">{line.description}</td>
                                    <td className="py-2 pr-3">{line.segment || "—"}</td>
                                    <td className="py-2">{line.series || "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                    <Button type="button" variant="ghost" size="icon" onClick={removeCoupon} aria-label="Remover cupom">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Input
                      value={couponCode}
                      onChange={event => {
                        setCouponCode(event.target.value.toUpperCase());
                        setCouponError(null);
                      }}
                      onKeyDown={event => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          void handleApplyCoupon();
                        }
                      }}
                      placeholder="Digite o código do cupom"
                      className={`font-mono uppercase ${couponError ? "border-destructive focus-visible:ring-destructive" : ""}`}
                    />
                    <Button type="button" variant="outline" onClick={handleApplyCoupon} disabled={isApplyingCoupon || total <= 0} className="sm:w-32">
                      {isApplyingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aplicar"}
                    </Button>
                  </div>
                )}
                {couponError && <p className="text-xs font-medium text-destructive">{couponError}</p>}
                <p className="text-xs text-muted-foreground">Cupom aplicado sobre os produtos. O frete não é alterado.</p>
              </div>
            )}

            {isCouponEnabled && !isScheduled && appliedCoupon && (
              <div className="space-y-1">
                <div className="flex justify-end text-lg font-semibold text-muted-foreground line-through decoration-primary decoration-2">
                  {formatCurrency(total + freteAnual + freteSemestral)}
                </div>
                <div className="flex items-center justify-between text-sm text-emerald-700">
                  <span>Desconto do cupom ({appliedCoupon.code})</span>
                  <span className="font-semibold">− {formatCurrency(appliedCouponNow)}</span>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center text-xl font-bold text-primary bg-primary/10 p-4 rounded-lg">
              <span>Total geral do pedido</span>
              <span>{formatCurrency(Math.max(0, (isScheduled ? total : total + freteAnual + freteSemestral) - (isCouponEnabled && !isScheduled ? appliedCouponNow : 0)))}</span>
            </div>
            {/* {!isScheduled && (
              <div className="text-xs text-muted-foreground italic mt-2">
                * Os valores exibidos não incluem itens do Pedido Programado (2º
                Semestre).
              </div>
            )} */}
            {usesDeferredSecondSemesterCharge && (
              <ScheduledItemsSection
                hasScheduledItems={hasScheduledItems}
                isExpanded={isProgramadosExpanded}
                onToggle={() => setIsProgramadosExpanded( !isProgramadosExpanded )}
                brandGroups={brandGroups}
                totalQuantity={totalQuantity}
                totalValue={totalValue}
                studentQuantity={studentQuantity}
                teacherQuantity={teacherQuantity}
                formatCurrency={formatCurrency}
                title="Pedido programado 2º semestre"
                tooltipText="Os valores exibidos não incluem itens do Pedido Programado (2º Semestre)."
                couponContent={isCouponEnabled && !isScheduled ? couponScheduledTable : null}
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Plano de Parcelas */}
      {!hideInstallmentPlan && !isEditMode && (
        <InstallmentPlanSection
          isScheduled={isScheduled}
          orderItems={chargeableOrderItems}
          freteAnual={freteAnual}
          freteSemestral={freteSemestral}
          couponDiscountAnnual={isCouponEnabled ? couponDiscountByBucket("Annual") : 0}
          couponDiscountSemestral={isCouponEnabled ? couponDiscountByBucket("Semestral") : 0}
          onAnnualInstallmentsChange={onAnnualInstallmentsChange}
          onSemestralInstallmentsChange={onSemestralInstallmentsChange}
          onAnnualConditionChange={onAnnualConditionChange}
          onSemestralConditionChange={onSemestralConditionChange}
          onLoadingStateChange={handleInstallmentLoadingStateChange}
          cycleYear={cycleYear}
        />
      )}

      {/* Finalizar Pedido */}
      {!hideFinalizeButton && (
        <Card className="shadow-lg border-primary/20">
          <CardContent className="p-6">
            <Button
              onClick={handleFinalizarClick}
              disabled={!isEditMode && (isInstallmentsLoading || !hasInstallmentData)}
              className="w-full h-12 text-lg font-semibold bg-primary hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isEditMode ? (
                "Salvar atualização"
              ) : isInstallmentsLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Carregando opções de pagamento...
                </span>
              ) : !hasInstallmentData ? (
                <span>Aguardando dados de parcelamento...</span>
              ) : (
                "🛒 Finalizar Pedido"
              )}
            </Button>
          </CardContent>
        </Card>
      )}
      <AlertDialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Finalizar Pedido Programado</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="flex flex-col gap-4 mt-2">
                <span className="flex flex-col gap-3 text-foreground">
                  <span>
                    Você está prestes a finalizar o seu <strong>Pedido Programado</strong>.
                  </span>
                  <span className="text-sm border-l-2 border-amber-500 pl-3 py-1 bg-amber-50/50 text-amber-800">
                    Esta ação enviará os itens para processamento e garantirá a reserva dos materiais para o próximo período.
                  </span>
                  <span>Deseja continuar?</span>
                </span>

                {isBackofficeEdit && (
                  <div className="space-y-2 pt-4 border-t">
                    <label className={cn(
                      "text-sm font-semibold",
                      reasonError ? "text-destructive" : "text-foreground"
                    )}>
                      Motivo da Edição <span className="text-destructive">*</span>
                    </label>
                    <Textarea
                      placeholder="Informe o motivo da edição..."
                      value={editReason}
                      onChange={(e) => {
                        setEditReason(e.target.value);
                        if (e.target.value.trim()) setReasonError(false);
                      }}
                      className={cn(
                        "min-h-[100px] transition-colors",
                        reasonError && "border-destructive focus-visible:ring-destructive"
                      )}
                    />
                    {reasonError && (
                      <p className="text-[11px] font-medium text-destructive">
                        O preenchimento deste campo é obrigatório.
                      </p>
                    )}
                    <p className="text-[11px] text-muted-foreground">
                      Esta justificativa é necessária para auditoria de edições do Backoffice.
                    </p>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <Button
              onClick={handleConfirmFinalize}
              className={cn(
                "bg-primary hover:bg-primary/90",
                isBackofficeEdit && !editReason.trim() && "opacity-80"
              )}
            >
              Sim, Finalizar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OrderSummary;
